import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Badge, Button, Group, Loader, Paper, Progress, Text, Tooltip } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'

import { formatDateTime } from '@/utils'
import { ArtifactsView } from '@/pages/Assistant/components/ArtifactsView'
import type { UsePatientRisksGapsResult } from '@/hooks/usePatientRisksGaps'
import { ClinicalSummaryLimitations } from '../ClinicalSummary/ClinicalSummaryLimitations'

// Shown centered while the first-ever report is being generated, until the agent's live
// thinking feed (beingSupersededThinking) starts arriving.
const REPORT_GENERATING_DEFAULT = 'Risks & Gaps report is being generated'

// The hook lives in the RisksGaps container (the status controls render in the shared
// header row, outside this panel), so both components receive its result as a prop.
type Props = {
  report: UsePatientRisksGapsResult
}

// Compact elapsed time since the rebuild started, e.g. "42s" / "3m 05s". The 2s
// being-superseded polling re-renders often enough to keep it ticking.
const elapsedSince = (iso: string): string => {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000))
  if (!Number.isFinite(seconds)) return ''
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`
}

// Rebuild progress (or staleness) + the rebuild trigger. Rendered by the page header;
// empty until the first read lands. Same anatomy as the overview's status controls —
// `upToDate` here covers both the chart AND the guideline corpus/citations.
export const RisksGapsStatus = ({ report }: Props) => {
  const { data, refreshing, requestRefresh } = report
  if (!data) return null

  // A rebuild of a report that's still on screen carries its live feedback in the
  // header; a first-ever build has nothing to show there yet, so the body's centered
  // indicator owns the explanation and the header stays minimal (just the elapsed timer).
  const isSubsequentRefresh = data.beingSuperseded && !!data.risksGapsReport
  const elapsed = data.beingSupersededSince ? elapsedSince(data.beingSupersededSince) : '…'

  return (
    <Group justify="flex-end" align="center" gap="sm" wrap="nowrap">
      <Group justify="flex-end" align="center" gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
        {data.beingSuperseded ? (
          <>
            {isSubsequentRefresh && (
              <Tooltip
                label={data.beingSupersededThinking}
                disabled={!data.beingSupersededThinking}
                multiline
                w={320}
                withArrow
              >
                <Text size="sm" c="dimmed" ta="right" truncate style={{ width: 100 }}>
                  {data.beingSupersededThinking ?? ''}
                </Text>
              </Tooltip>
            )}
            <Loader size="xs" />
            <Text size="sm" c="dimmed" className="whitespace-nowrap">
              {elapsed}
            </Text>
          </>
        ) : (
          <>
            {data.lastBuildFailed ? (
              // The last rebuild failed: shown IN PLACE of the staleness badge.
              // Automatic rebuilds are stopped server-side; the Refresh button retries.
              <Tooltip
                label={`${data.lastBuildFailed.reason} — ${formatDateTime(data.lastBuildFailed.at, '/')}`}
                withArrow
              >
                <Badge color="red" variant="light">
                  Failed to refresh
                </Badge>
              </Tooltip>
            ) : data.upToDate === undefined ? (
              // The fast read skips the staleness check; the follow-up verifying call
              // fills upToDate in.
              <Badge color="blue" variant="light">
                Verifying
              </Badge>
            ) : (
              <Badge color={data.upToDate ? 'green' : 'yellow'} variant="light">
                {data.upToDate ? 'Up to date' : 'Out of date'}
              </Badge>
            )}
          </>
        )}
      </Group>
      <Button
        size="sm"
        variant="default"
        onClick={requestRefresh}
        loading={refreshing}
        disabled={data.beingSuperseded}
        leftSection={<IconRefresh size={14} />}
      >
        Refresh
      </Button>
      {data.risksGapsReport && (
        <Text size="sm" c="dimmed" className="whitespace-nowrap">
          Generated: {formatDateTime(data.risksGapsReport.generatedAt, '/')}
        </Text>
      )}
    </Group>
  )
}

// The stored AI-generated report (headline + the High-Priority Risks / Preventive Care &
// Screening Gaps tables) rendered through the assistant's shared artifact renderer.
// Suggestions are hidden — this is a read-only surface with no chat to follow up in.
export const RisksGapsReportPanel = ({ report }: Props) => {
  const { data, loading, error, refresh } = report
  const stored = data?.risksGapsReport

  // A `#visit-topic-…` link (the daily briefing) scrolls to that Visit Guidance topic once
  // the report shows it and marks it for a moment; polls of the report don't scroll again.
  const { hash } = useLocation()
  const topicAnchor = hash.startsWith('#visit-topic-') ? hash.slice(1) : null
  const scrolledTo = useRef<string | null>(null)
  useEffect(() => {
    if (!topicAnchor || !stored || scrolledTo.current === topicAnchor) return
    const topic = document.getElementById(topicAnchor)
    if (!topic) return
    scrolledTo.current = topicAnchor
    topic.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const mark = ['ring-2', 'ring-[#e55535]']
    topic.classList.add(...mark)
    const unmark = () => topic.classList.remove(...mark)
    const timer = setTimeout(unmark, 2500)
    return () => {
      clearTimeout(timer)
      unmark()
    }
  }, [topicAnchor, stored])

  if (loading && !data) {
    return <p className="py-12 text-center text-[#7D7987]">Loading report...</p>
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-red-600">{error}</p>
        <Button variant="default" onClick={refresh} leftSection={<IconRefresh size={14} />}>
          Retry
        </Button>
      </div>
    )
  }

  if (!data) return null

  if (!stored) {
    // First-ever build: the panel would otherwise sit blank for minutes, so show a
    // centered progress bar + the agent's live thinking. The spinner lives in the header
    // (next to the timer/button), not here.
    if (data.beingSuperseded) {
      return (
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <Progress value={100} animated className="w-full max-w-xs" />
          <Text size="sm" c="dimmed" className="line-clamp-4 max-w-xl">
            {data.beingSupersededThinking ?? REPORT_GENERATING_DEFAULT}
          </Text>
        </div>
      )
    }
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed">
          No Risks & Gaps report has been generated for this patient yet.
        </Text>
      </Paper>
    )
  }

  const guidelines = stored.guidelinesMeta
  return (
    // ArtifactsView returns a fragment; the caller supplies the flex column. The
    // "Generated:" timestamp lives in the header status bar.
    <div className="flex flex-col gap-3">
      {/* Same data-completeness banner as the Clinical Summary tabs, driven by the meta
          of the chart details this report was built from. Self-hides when complete;
          absent on placeholders. */}
      {stored.detailsMeta && <ClinicalSummaryLimitations meta={stored.detailsMeta} />}
      <ArtifactsView artifacts={stored.artifacts} showSuggestions={false} />
      {/* Guideline provenance strip: what the deterministic selection fed the report. */}
      {guidelines && (
        <Text size="xs" c="dimmed">
          Based on {guidelines.selectedDocuments.length} guideline document
          {guidelines.selectedDocuments.length === 1 ? '' : 's'}
          {guidelines.matchedFamilies.length > 0 &&
            ` across ${guidelines.matchedFamilies.length} matched famil${guidelines.matchedFamilies.length === 1 ? 'y' : 'ies'}`}
          {guidelines.searchedDocuments.length > 0 &&
            `, plus ${guidelines.searchedDocuments.length} found via search`}
          .
        </Text>
      )}
    </div>
  )
}
